import { NavLink } from 'react-router-dom';

export interface PageTab {
  to: string;
  label: string;
  /** Match this route exactly (use on the section's index tab). */
  end?: boolean;
}

/**
 * Segmented sub-navigation that groups related pages under one section — Files/Jobs
 * and Audit/Unpaid. Each tab routes to its own page (routes are unchanged, so deep
 * links still work); the active route is underlined. Stands in for a page <h1> as
 * the section header, so the primary nav can stay uncrowded.
 */
export function PageTabs({ tabs }: { tabs: PageTab[] }) {
  return (
    <div
      className="flex gap-1 border-b border-neutral-200 dark:border-neutral-800"
      role="tablist"
      aria-label="Section"
    >
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            `-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
              isActive
                ? 'border-emerald-600 text-neutral-900 dark:text-white'
                : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
            }`
          }
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}

/** Files section: statements + the processing jobs they spawn. */
export const FILES_TABS: PageTab[] = [
  { to: '/files', label: 'Files', end: true },
  { to: '/jobs', label: 'Jobs' },
];

/** Audit section: cumulative audit totals + unpaid-member reconciliation. */
export const AUDIT_TABS: PageTab[] = [
  { to: '/audit', label: 'Audit', end: true },
  { to: '/unpaid', label: 'Unpaid members' },
];

/**
 * Chargebacks section: clawbacks + the entitlement-gated disputes tracker and
 * proof-out report. Tabs reflect what the account is entitled to; render the bar
 * only when more than one tab is visible.
 */
export function chargebacksTabs(entitlements: Record<string, boolean>): PageTab[] {
  const tabs: PageTab[] = [{ to: '/chargebacks', label: 'Chargebacks', end: true }];
  if (entitlements.chargebackProof) tabs.push({ to: '/chargebacks/proof', label: 'Proof-out' });
  if (entitlements.paymentGaps) tabs.push({ to: '/chargebacks/gaps', label: 'Payment gaps' });
  if (entitlements.disputes) tabs.push({ to: '/disputes', label: 'Disputes' });
  return tabs;
}
